import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  X,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Clock,
  Shield,
  Zap,
  Globe,
  Building2,
  Calculator,
  PiggyBank,
  Award,
  Target,
  BarChart3,
  FileText,
  Calendar,
  CreditCard,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Presentation,
  Play,
  Pause,
  RotateCcw,
  Star,
  Heart,
  Sparkles,
  Rocket,
  BadgeCheck,
  AlertTriangle,
  Ban,
  Infinity,
  Code,
  Server,
  Database,
  Briefcase,
  Phone,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogoFullHorizontal } from '../components/Logo';

const PITCH_PASSWORD = '8269';

export default function SalesPitch() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check localStorage for previous authentication
  useEffect(() => {
    if (localStorage.getItem('sales_pitch_access') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlay && isAuthenticated) {
      interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
      }, 8000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlay, isAuthenticated]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === PITCH_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('sales_pitch_access', 'true');
      setError('');
    } else {
      setError('Invalid password. Please contact your sales manager.');
    }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goToSlide = (index) => setCurrentSlide(index);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Slide content
  const slides = [
    // Slide 1: Title
    {
      id: 'title',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="mb-8">
            <LogoFullHorizontal className="h-16 mx-auto" />
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6">
            Stop Paying Rent for
            <span className="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Your HR System
            </span>
          </h1>
          <p className="text-2xl text-slate-300 max-w-3xl">
            The Complete HR Management System You Can Own Forever
          </p>
          <div className="mt-12 flex items-center gap-4 text-slate-400">
            <span className="flex items-center gap-2"><Code className="h-5 w-5" /> Full Source Code</span>
            <span className="text-slate-600">•</span>
            <span className="flex items-center gap-2"><Shield className="h-5 w-5" /> Malaysia Compliant</span>
            <span className="text-slate-600">•</span>
            <span className="flex items-center gap-2"><Infinity className="h-5 w-5" /> Perpetual License</span>
          </div>
        </div>
      )
    },
    // Slide 2: The Problem
    {
      id: 'problem',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full text-red-400 text-sm font-semibold mb-4">
              <AlertTriangle className="h-4 w-4 mr-2" />
              The Problem
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              SaaS HR Systems Are Draining Your Budget
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 flex-1">
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 flex flex-col">
              <DollarSign className="h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Never-Ending Payments</h3>
              <p className="text-slate-400 text-lg">
                Typical SaaS: <span className="text-red-400 font-bold">RM 15-50/employee/month</span>
              </p>
              <p className="text-slate-500 mt-2">
                100 employees = RM 18,000-60,000/year... forever.
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 flex flex-col">
              <Lock className="h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Vendor Lock-In</h3>
              <p className="text-slate-400 text-lg">
                Your data is <span className="text-red-400 font-bold">held hostage</span>
              </p>
              <p className="text-slate-500 mt-2">
                Stop paying? Lose access to everything.
              </p>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 flex flex-col">
              <Ban className="h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">No Customization</h3>
              <p className="text-slate-400 text-lg">
                <span className="text-red-400 font-bold">Take it or leave it</span>
              </p>
              <p className="text-slate-500 mt-2">
                Can't modify features to fit your unique processes.
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 3: The Solution
    {
      id: 'solution',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-semibold mb-4">
              <Sparkles className="h-4 w-4 mr-2" />
              The Solution
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Own Your HR System. Forever.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 flex-1">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 flex flex-col">
              <PiggyBank className="h-12 w-12 text-emerald-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">One-Time Payment</h3>
              <p className="text-slate-400 text-lg">
                Pay once: <span className="text-emerald-400 font-bold">RM 18,000 - 128,000</span>
              </p>
              <p className="text-slate-500 mt-2">
                No monthly fees. No per-user charges. Implementation included.
              </p>
            </div>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 flex flex-col">
              <Code className="h-12 w-12 text-emerald-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Complete Source Code</h3>
              <p className="text-slate-400 text-lg">
                <span className="text-emerald-400 font-bold">100% ownership</span> of code
              </p>
              <p className="text-slate-500 mt-2">
                Modify, customize, and extend as needed.
              </p>
            </div>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 flex flex-col">
              <Server className="h-12 w-12 text-emerald-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">Your Infrastructure</h3>
              <p className="text-slate-400 text-lg">
                Deploy <span className="text-emerald-400 font-bold">anywhere you want</span>
              </p>
              <p className="text-slate-500 mt-2">
                Your servers. Your data. Your control.
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: ROI Calculator
    {
      id: 'roi',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-semibold mb-4">
              <Calculator className="h-4 w-4 mr-2" />
              ROI Analysis
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Save Up To <span className="text-emerald-400">RM 580,000</span> in 5 Years
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 flex-1">
            {/* SaaS Cost */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">SaaS Model</h3>
                <TrendingUp className="h-8 w-8 text-red-400" />
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">200 employees × RM 30/month</span>
                  <span className="text-white font-semibold">RM 6,000/month</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 1</span>
                  <span className="text-red-400 font-semibold">RM 72,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 3</span>
                  <span className="text-red-400 font-semibold">RM 216,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 5</span>
                  <span className="text-red-400 font-bold text-xl">RM 360,000</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-400">10 Years Total</span>
                  <span className="text-red-400 font-bold text-2xl">RM 720,000</span>
                </div>
              </div>
            </div>
            
            {/* MyHR Orbit */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">MyHR Orbit</h3>
                <TrendingDown className="h-8 w-8 text-emerald-400" />
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">One-time Business License</span>
                  <span className="text-white font-semibold">RM 110,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 1</span>
                  <span className="text-emerald-400 font-semibold">RM 110,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 3</span>
                  <span className="text-emerald-400 font-semibold">RM 110,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Year 5</span>
                  <span className="text-emerald-400 font-bold text-xl">RM 110,000</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-emerald-500/20 rounded-lg px-4 -mx-4">
                  <span className="text-white font-semibold">10 Years Total</span>
                  <span className="text-emerald-400 font-bold text-2xl">RM 110,000</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-emerald-500/20 rounded-xl text-center">
                <p className="text-emerald-400 text-lg font-semibold">10-Year Savings</p>
                <p className="text-4xl font-bold text-white">RM 610,000</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 5: Features
    {
      id: 'features',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm font-semibold mb-4">
              <Zap className="h-4 w-4 mr-2" />
              Complete Feature Set
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Everything You Need. Nothing You Don't.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 flex-1">
            {[
              { icon: Users, title: 'Employee Management', desc: 'Complete profiles, org charts, departments' },
              { icon: Calendar, title: 'Leave Management', desc: 'Custom leave types, approvals, balances' },
              { icon: Clock, title: 'Attendance', desc: 'Clock in/out, schedules, overtime' },
              { icon: CreditCard, title: 'Payroll', desc: 'Malaysian tax compliant, EPF, SOCSO, EIS' },
              { icon: FileText, title: 'Claims & Expenses', desc: 'Submit, approve, reimburse' },
              { icon: BarChart3, title: 'Performance', desc: 'Reviews, goals, KPIs' },
              { icon: FileText, title: 'Digital Signatures', desc: 'DocuSign-like e-signatures' },
              { icon: Globe, title: 'Multi-language', desc: 'English, Malay, Chinese support' },
              { icon: Shield, title: 'Role-Based Access', desc: 'Admin, HR, Manager, Employee' },
              { icon: Building2, title: 'Multi-Company', desc: 'Manage multiple entities' },
              { icon: Target, title: 'Reports & Analytics', desc: 'Excel, PDF exports, dashboards' },
              { icon: Rocket, title: 'EA Form Generation', desc: 'Malaysian tax forms ready' },
            ].map((feature, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-purple-500/50 transition-colors">
                <feature.icon className="h-8 w-8 text-purple-400 mb-3" />
                <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Slide 6: Tech Stack
    {
      id: 'tech',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold mb-4">
              <Code className="h-4 w-4 mr-2" />
              Modern Tech Stack
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Built With Industry Best Practices
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 flex-1 max-w-5xl mx-auto w-full">
            <div className="bg-gradient-to-b from-cyan-900/30 to-slate-900 border border-cyan-500/30 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Code className="h-10 w-10 text-cyan-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Frontend</h3>
              <div className="space-y-2 text-slate-300">
                <p className="font-semibold text-cyan-400">React 18</p>
                <p>Tailwind CSS</p>
                <p>Shadcn/UI Components</p>
                <p>React Router v6</p>
                <p>Responsive Design</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-emerald-900/30 to-slate-900 border border-emerald-500/30 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Server className="h-10 w-10 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Backend</h3>
              <div className="space-y-2 text-slate-300">
                <p className="font-semibold text-emerald-400">FastAPI (Python)</p>
                <p>JWT Authentication</p>
                <p>OAuth2 / Google SSO</p>
                <p>REST API</p>
                <p>File Upload Support</p>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-purple-900/30 to-slate-900 border border-purple-500/30 rounded-2xl p-8 text-center">
              <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Database className="h-10 w-10 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Database</h3>
              <div className="space-y-2 text-slate-300">
                <p className="font-semibold text-purple-400">MongoDB</p>
                <p>Scalable NoSQL</p>
                <p>Easy Backup/Restore</p>
                <p>Flexible Schema</p>
                <p>Cloud or On-Premise</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 7: Malaysia Compliance
    {
      id: 'compliance',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-semibold mb-4">
              🇲🇾 Malaysia First
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              100% Malaysia Statutory Compliant
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 flex-1 max-w-5xl mx-auto w-full">
            <div className="space-y-4">
              {[
                { title: 'EPF (KWSP)', desc: 'Employee & employer contribution calculations' },
                { title: 'SOCSO (PERKESO)', desc: 'Employment Injury & Invalidity schemes' },
                { title: 'EIS', desc: 'Employment Insurance System deductions' },
                { title: 'PCB (MTD)', desc: 'Monthly Tax Deduction calculations' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white text-lg">{item.title}</h3>
                    <p className="text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="space-y-4">
              {[
                { title: 'EA Form Generation', desc: 'Annual tax statement for employees' },
                { title: 'CP39 Reports', desc: 'Monthly PCB remittance reports' },
                { title: 'Public Holidays', desc: 'Malaysian federal & state holidays' },
                { title: 'Leave Policies', desc: 'Annual, medical, maternity as per law' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <CheckCircle className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white text-lg">{item.title}</h3>
                    <p className="text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    // Slide 8: Pricing
    {
      id: 'pricing',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-semibold mb-4">
              <DollarSign className="h-4 w-4 mr-2" />
              All-Inclusive Pricing
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              One-Time Payment. Everything Included.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4 flex-1 max-w-6xl mx-auto w-full">
            {/* Starter */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 flex flex-col">
              <div className="mb-3">
                <h3 className="text-lg font-bold text-white">Starter</h3>
                <p className="text-slate-400 text-xs">Up to 30 Employees</p>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">RM 18,000</span>
              </div>
              <ul className="space-y-2 flex-1">
                {['Complete Source Code', 'Perpetual License', 'Implementation Included', 'Email Support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Growth - Popular */}
            <div className="bg-gradient-to-b from-emerald-900/30 to-slate-800/50 border-2 border-emerald-500 rounded-2xl p-5 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>
              </div>
              <div className="mb-3">
                <h3 className="text-lg font-bold text-white">Growth</h3>
                <p className="text-slate-400 text-xs">Up to 100 Employees</p>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold text-emerald-400">RM 38,000</span>
              </div>
              <ul className="space-y-2 flex-1">
                {['Everything in Starter', 'Assisted Deployment', 'Video Training (4 hrs)', 'Custom Branding', '30-Day Support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Business */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 flex flex-col">
              <div className="mb-3">
                <h3 className="text-lg font-bold text-white">Business</h3>
                <p className="text-slate-400 text-xs">Up to 300 Employees</p>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">RM 68,000</span>
              </div>
              <ul className="space-y-2 flex-1">
                {['Everything in Growth', 'On-site Training (1 day)', 'Custom Dev (8 hrs)', '60-Day Support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Enterprise */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 flex flex-col">
              <div className="mb-3">
                <h3 className="text-lg font-bold text-white">Enterprise</h3>
                <p className="text-slate-400 text-xs">Unlimited Employees</p>
              </div>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">RM 128,000</span>
              </div>
              <ul className="space-y-2 flex-1">
                {['Everything in Business', 'On-site Training (3 days)', 'Custom Dev (24 hrs)', '90-Day Support'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 9: Why Choose Us
    {
      id: 'why',
      content: (
        <div className="flex flex-col h-full px-12 py-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-sm font-semibold mb-4">
              <Star className="h-4 w-4 mr-2" />
              Why MyHR Orbit?
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              The Smart Choice for Malaysian Businesses
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 flex-1 max-w-5xl mx-auto w-full">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <PiggyBank className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Massive Cost Savings</h3>
                  <p className="text-slate-400">Save RM 500,000+ over 10 years compared to SaaS subscriptions. ROI in under 2 years.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Code className="h-6 w-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Full Customization</h3>
                  <p className="text-slate-400">Modify anything. Add features. Integrate with your existing systems. It's your code.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Data Sovereignty</h3>
                  <p className="text-slate-400">Your employee data stays on your servers. Full control. Full privacy. Full compliance.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Globe className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Malaysia-First Design</h3>
                  <p className="text-slate-400">Built specifically for Malaysian statutory requirements. EPF, SOCSO, EIS, PCB ready.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Infinity className="h-6 w-6 text-rose-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Perpetual License</h3>
                  <p className="text-slate-400">Use it forever. No expiration. No renewal fees. Pass it down through company changes.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Rocket className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Modern Tech Stack</h3>
                  <p className="text-slate-400">React, FastAPI, MongoDB. Industry-standard. Easy to find developers. Easy to maintain.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 10: CTA
    {
      id: 'cta',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="mb-8">
            <LogoFullHorizontal className="h-12 mx-auto" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            Ready to Own Your
            <span className="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              HR System?
            </span>
          </h1>
          <p className="text-2xl text-slate-300 max-w-2xl mb-12">
            Let's discuss your requirements and get you a custom quote.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-8 py-6 text-center">
              <Phone className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm mb-1">Call Us</p>
              <p className="text-white font-semibold text-lg">+60 12-345 6789</p>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl px-8 py-6 text-center">
              <Mail className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm mb-1">Email Us</p>
              <p className="text-white font-semibold text-lg">sales@myhrorbit.com</p>
            </div>
            <div className="bg-emerald-500/20 border border-emerald-500 rounded-xl px-8 py-6 text-center">
              <Presentation className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-400 text-sm mb-1">Live Demo</p>
              <p className="text-emerald-400 font-semibold text-lg">Schedule Today</p>
            </div>
          </div>
          
          <p className="text-slate-500 mt-12 text-sm">
            Perpetual, Non-Exclusive Source Code License • Made with ❤️ in Malaysia
          </p>
        </div>
      )
    }
  ];

  // Password protection screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <LogoFullHorizontal className="h-10 mx-auto mb-6" />
            <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-semibold mb-4">
              <Lock className="h-4 w-4 mr-2" />
              Sales Team Only
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Sales Pitch Deck</h1>
            <p className="text-slate-400">Enter the access password to view the presentation.</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-slate-800/50 border-slate-700 text-white h-12 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg"
            >
              <Unlock className="h-4 w-4 mr-2" />
              Access Presentation
            </Button>
          </form>
          
          <div className="mt-8 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Website
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Presentation view
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Top Controls */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Exit
            </Button>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400 text-sm">
              Slide {currentSlide + 1} of {slides.length}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentSlide(0)}
              className="text-slate-400 hover:text-white"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAutoPlay(!isAutoPlay)}
              className={isAutoPlay ? 'text-emerald-400' : 'text-slate-400 hover:text-white'}
            >
              {isAutoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="text-slate-400 hover:text-white"
            >
              <Presentation className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 pt-16 pb-24">
        <div className="h-full max-w-7xl mx-auto">
          {slides[currentSlide].content}
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-950/90 backdrop-blur-xl border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className="text-slate-400 hover:text-white disabled:opacity-50"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Previous
            </Button>
            
            {/* Slide indicators */}
            <div className="flex items-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'bg-emerald-500 w-8' 
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
            
            <Button
              variant="ghost"
              onClick={nextSlide}
              disabled={currentSlide === slides.length - 1}
              className="text-slate-400 hover:text-white disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
